/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { createSelector } from '@ngrx/store';
import { selectInstanceState } from './feature.selector';
import { PagedGrid } from '../../../models';
import { PagedGridState } from '../reducer';

function isGridLoading(state: PagedGridState): boolean {
  return !state.chunks?.length || !state.columnsData || (state.chunks.length === 1 && state.chunks[0].loading);
}

function generateLoadingGrid(): PagedGrid.ComputedGridModel {
  const loadingSubRows = new Array(50).fill(0).map(
    (_, rowIndex) =>
      ({
        rowIndex,
        rowId: `loading-${rowIndex}`,
        subRowIndex: rowIndex,
        subRowId: `subrow-${rowIndex}`,
        subRowType: PagedGrid.SubRowType.Data,
        loading: true,
        subRowData: {},
      }) as PagedGrid.BaseGridChunkSubRowMetadata,
  );

  return {
    columnsData: new Array(5).fill(0).map((_, index) => ({
      columnId: `loading-${index}`,
      columnName: 'loading',
      columnDescription: null,
      sortable: false,
    })),
    subRows: loadingSubRows,
    loading: true,
    viewId: 'initial-loading-view',
    totalRows: 0,
  };
}

function generateDataViewId(state: PagedGridState): string {
  return `${state.viewId}-${state.pageInfo.totalRows}-${state.pageInfo.currentPageIndex}-${state.columnsData.map((columnData) => columnData.columnId).join('-')}`;
}

export const selectGridView = (instanceId: string) => createSelector(selectInstanceState(instanceId), (state) => {
  if (!state || !state.pageInfo) {
    return;
  }
  
  if (isGridLoading(state)) {
    return generateLoadingGrid();
  }

  const result = [];
  let rowIndexOffset = state.pageInfo.currentPageIndex * state.pageInfo.rowsPerPage;
  let subRowIndex = 0;

  state.chunks.forEach((chunk, chunkIndex) => {
    chunk.rows?.forEach((row, rowIndexInChunk) => {
      row.rowIndex = rowIndexOffset;
      row.loading = chunk.loadingIndicator;
      row.chunkIndex = chunk.index;
      row.rowIndexInChunk = rowIndexInChunk;
      row.rowIndexInChunk = rowIndexInChunk;

      const rowRelatedSubRows = row.displayedSubRows.map((subRow: PagedGrid.BaseGridChunkSubRowMetadata, index) => {
        subRow.index = subRowIndex + index;
        subRow.chunkIndex = chunkIndex;
        subRow.rowIndex = row.rowIndex;
        subRow.rowId = row.rowId;
        subRow.loading = row.loading;
        subRow.subRowIndex = index;
        subRow.rowIndexInChunk = row.rowIndexInChunk;
        return subRow;
      });
      result.push(...rowRelatedSubRows);
      subRowIndex += row.displayedSubRows.length;
      rowIndexOffset++;
    });
  });

  return {
    subRows: result,
    columnsData: state.columnsData,
    viewId: generateDataViewId(state),
    loading: false,
    totalRows: state.pageInfo.totalRows,
  } as PagedGrid.ComputedGridModel;
});
